import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shirt, MapPin, Clock, DollarSign, ArrowLeft, ArrowRight,
  CheckCircle, User, Phone, AlertCircle, Package, Sparkles,
  Zap, Shield, Star, Droplets
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { toast } from 'sonner';

const LaundryBookingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [priceEstimate, setPriceEstimate] = useState(null);
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    pickupAddress: '',
    pickupCity: '',
    pickupZip: '',
    pickupInstructions: '',
    sameAsPickup: true,
    returnAddress: '',
    returnCity: '',
    returnZip: '',
    numberOfBags: '1',
    serviceType: 'wash_fold',
    specialItems: [],
    preferences: '',
    pickupDate: '',
    pickupTime: '',
    returnPreference: 'next_day'
  });

  const serviceTypes = [
    { 
      id: 'wash_fold', 
      label: 'Wash & Fold', 
      price: '$2.50', 
      unit: '/lb', 
      icon: '🧺', 
      description: 'Standard wash, dry, and fold',
      color: '#8B5CF6'
    },
    { 
      id: 'wash_iron', 
      label: 'Wash & Iron', 
      price: '$3.50', 
      unit: '/lb', 
      icon: '👔', 
      description: 'Wash, dry, and iron',
      color: '#06B6D4'
    },
    { 
      id: 'dry_clean', 
      label: 'Dry Cleaning', 
      price: 'varies', 
      unit: '', 
      icon: '✨', 
      description: 'Professional dry clean',
      color: '#D4AF37'
    }
  ];

  const returnOptions = [
    { id: 'same_day', label: 'Same Day', description: 'Rush service', fee: 15, badge: 'EXPRESS' },
    { id: 'next_day', label: 'Next Day', description: 'Standard turnaround', fee: 0, badge: 'POPULAR' },
    { id: 'two_day', label: '2-3 Days', description: 'Economy option', fee: 0, badge: null }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateEstimate = async () => {
    setLoading(true);
    try {
      const response = await api.post('/pricing/estimate', {
        service_type: 'laundry',
        laundry_type: formData.serviceType,
        bags: parseInt(formData.numberOfBags),
        return_preference: formData.returnPreference
      });
      setPriceEstimate(response.data);
      setStep(4);
    } catch (error) {
      const bags = parseInt(formData.numberOfBags) || 1;
      const estimatedWeight = bags * 10;
      const pricePerLb = formData.serviceType === 'wash_fold' ? 2.50 : 
                         formData.serviceType === 'wash_iron' ? 3.50 : 5.00;
      const rushFee = formData.returnPreference === 'same_day' ? 15 : 0;
      const pickupFee = 10;
      
      setPriceEstimate({
        estimated_weight: estimatedWeight,
        price_per_lb: pricePerLb,
        laundry_cost: estimatedWeight * pricePerLb,
        pickup_delivery_fee: pickupFee,
        rush_fee: rushFee,
        total: (estimatedWeight * pricePerLb) + pickupFee + rushFee
      });
      setStep(4);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const returnAddr = formData.sameAsPickup 
        ? `Same as pickup` 
        : `${formData.returnAddress}, ${formData.returnCity}, NC ${formData.returnZip}`;

      const response = await api.post('/checkout/guest/initiate', {
        service_type: 'laundry',
        service_name: 'Laundry Pickup & Delivery',
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        address: formData.pickupAddress,
        city: formData.pickupCity,
        state: 'NC',
        zip_code: formData.pickupZip,
        scheduled_date: formData.pickupDate,
        scheduled_time: formData.pickupTime,
        special_instructions: `
SERVICE TYPE: ${serviceTypes.find(t => t.id === formData.serviceType)?.label}
BAGS: ${formData.numberOfBags} (estimated ~${parseInt(formData.numberOfBags) * 10} lbs)
RETURN: ${returnOptions.find(o => o.id === formData.returnPreference)?.label}

PICKUP ADDRESS:
${formData.pickupAddress}, ${formData.pickupCity}, NC ${formData.pickupZip}
${formData.pickupInstructions ? 'Notes: ' + formData.pickupInstructions : ''}

RETURN TO: ${returnAddr}

${formData.preferences ? 'PREFERENCES: ' + formData.preferences : ''}
        `.trim(),
        total_amount: priceEstimate?.total || 45,
        payment_method: 'card'
      });

      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data?.pending_id) {
        navigate(`/guest-checkout?pending=${response.data.pending_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: 'Your Info', icon: User },
    { num: 2, label: 'Address', icon: MapPin },
    { num: 3, label: 'Laundry', icon: Shirt },
    { num: 4, label: 'Confirm', icon: CheckCircle }
  ];

  const selectedService = serviceTypes.find(t => t.id === formData.serviceType);

  return (
    <div className="min-h-screen service-bg-laundry relative overflow-hidden">
      {/* Animated Background Glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/15 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      {/* Header */}
      <div className="sticky-header px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="w-10 h-10 rounded-xl glass-card flex items-center justify-center text-slate-400 hover:text-white transition-colors"
            data-testid="laundry-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 rounded-2xl icon-gradient-laundry flex items-center justify-center shadow-lg glow-laundry">
            <Shirt className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white font-['Plus_Jakarta_Sans']">Laundry</h1>
            <h1 className="text-xl font-bold text-white font-['Plus_Jakarta_Sans']">Service</h1>
          </div>
        </div>
      </div>

      {/* Premium Service Banner */}
      <div className="max-w-lg mx-auto px-4 mt-4 mb-6">
        <div className="premium-banner flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <span className="text-purple-400 font-semibold">Premium Service</span>
            <span className="text-slate-400 ml-2">— Professional wash & fold with free pickup and delivery</span>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-lg mx-auto px-4 py-4">
        <div className="relative flex items-center justify-between">
          {/* Connecting Line */}
          <div className="step-connector" />
          <div 
            className="step-connector-active" 
            style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%`, background: 'linear-gradient(90deg, #8B5CF6, #6366F1)' }} 
          />
          
          {steps.map((s, idx) => (
            <motion.div 
              key={s.num}
              className="flex flex-col items-center relative z-10"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.1 }}
            >
              <div 
                className={`step-node ${
                  step > s.num ? 'step-node-completed' : 
                  step === s.num ? 'step-node-active' : 'step-node-pending'
                }`}
                data-testid={`laundry-step-${s.num}`}
              >
                {step > s.num ? <CheckCircle className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-lg mx-auto px-4 pb-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
            className="glass-card p-6 sm:p-8"
          >
            {/* Step 1: Customer Info */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl icon-gradient-laundry flex items-center justify-center glow-laundry">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-100 font-['Plus_Jakarta_Sans']">Your Information</h2>
                    <p className="text-slate-400 text-sm">Tell us how to reach you</p>
                  </div>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">Full Name *</label>
                    <Input
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="dark-input h-12"
                      data-testid="laundry-customer-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">Phone *</label>
                    <Input
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="dark-input h-12"
                      data-testid="laundry-customer-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">Email *</label>
                    <Input
                      name="customerEmail"
                      type="email"
                      value={formData.customerEmail}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="dark-input h-12"
                      data-testid="laundry-customer-email-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Address */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8B5CF6]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#8B5CF6]/30">
                    <MapPin className="w-6 h-6 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Pickup & Return</h2>
                    <p className="text-white/50 text-sm">Where should we pick up your laundry?</p>
                  </div>
                </div>
                
                {/* Pickup Address */}
                <div className="bg-gradient-to-br from-[#8B5CF6]/10 to-[#F4C2C2]/10 border border-[#8B5CF6]/30 rounded-2xl p-5">
                  <h3 className="text-[#8B5CF6] font-semibold mb-4 flex items-center gap-2">
                    <span className="text-xs px-2 py-1 bg-[#8B5CF6]/20 rounded-full">PICKUP ADDRESS</span>
                  </h3>
                  <div className="space-y-3">
                    <Input
                      name="pickupAddress"
                      value={formData.pickupAddress}
                      onChange={handleChange}
                      placeholder="Street Address"
                      className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                      data-testid="pickup-address-input"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        name="pickupCity"
                        value={formData.pickupCity}
                        onChange={handleChange}
                        placeholder="City"
                        className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                      />
                      <Input
                        name="pickupZip"
                        value={formData.pickupZip}
                        onChange={handleChange}
                        placeholder="ZIP Code"
                        className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                      />
                    </div>
                    <Input
                      name="pickupInstructions"
                      value={formData.pickupInstructions}
                      onChange={handleChange}
                      placeholder="Pickup instructions (e.g., leave at door)"
                      className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                    />
                  </div>
                </div>

                {/* Same as Pickup Toggle */}
                <label className="flex items-center gap-4 p-4 bg-white/[0.02] border border-white/10 rounded-xl cursor-pointer hover:bg-white/[0.05] transition-colors">
                  <input
                    type="checkbox"
                    name="sameAsPickup"
                    checked={formData.sameAsPickup}
                    onChange={handleChange}
                    className="w-5 h-5 rounded-md border-white/30 bg-transparent text-[#8B5CF6] focus:ring-[#8B5CF6]/20"
                    data-testid="same-as-pickup-checkbox"
                  />
                  <div>
                    <span className="text-white font-medium">Return to same address</span>
                    <p className="text-white/40 text-xs mt-0.5">We'll bring your clean laundry back here</p>
                  </div>
                </label>

                {/* Return Address (if different) */}
                <AnimatePresence>
                  {!formData.sameAsPickup && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#F4C2C2]/10 border border-[#D4AF37]/30 rounded-2xl p-5">
                        <h3 className="text-[#D4AF37] font-semibold mb-4 flex items-center gap-2">
                          <span className="text-xs px-2 py-1 bg-[#D4AF37]/20 rounded-full">RETURN ADDRESS</span>
                        </h3>
                        <div className="space-y-3">
                          <Input
                            name="returnAddress"
                            value={formData.returnAddress}
                            onChange={handleChange}
                            placeholder="Street Address"
                            className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <Input
                              name="returnCity"
                              value={formData.returnCity}
                              onChange={handleChange}
                              placeholder="City"
                              className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                            />
                            <Input
                              name="returnZip"
                              value={formData.returnZip}
                              onChange={handleChange}
                              placeholder="ZIP Code"
                              className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Step 3: Laundry Details */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8B5CF6]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#8B5CF6]/30">
                    <Shirt className="w-6 h-6 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Laundry Details</h2>
                    <p className="text-white/50 text-sm">What service do you need?</p>
                  </div>
                </div>
                
                {/* Service Type Selection */}
                <div>
                  <label className="text-white/70 text-sm mb-3 block font-medium">Service Type *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {serviceTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setFormData(prev => ({ ...prev, serviceType: type.id }))}
                        className={`p-5 rounded-2xl border text-left transition-all duration-300 relative overflow-hidden ${
                          formData.serviceType === type.id
                            ? 'border-[#8B5CF6] shadow-lg shadow-[#8B5CF6]/20'
                            : 'bg-white/[0.02] border-white/10 hover:border-white/30 hover:bg-white/[0.05]'
                        }`}
                        style={{
                          background: formData.serviceType === type.id 
                            ? `linear-gradient(135deg, ${type.color}20, ${type.color}10)` 
                            : undefined
                        }}
                        data-testid={`service-type-${type.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <span className="text-3xl">{type.icon}</span>
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                            formData.serviceType === type.id ? 'bg-white/20 text-white' : 'bg-white/10 text-white/60'
                          }`}>
                            {type.price}{type.unit}
                          </span>
                        </div>
                        <p className={`font-semibold mt-3 ${formData.serviceType === type.id ? 'text-white' : 'text-white/80'}`}>
                          {type.label}
                        </p>
                        <p className={`text-xs mt-1 ${formData.serviceType === type.id ? 'text-white/70' : 'text-white/40'}`}>
                          {type.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Number of Bags */}
                <div>
                  <label className="text-white/70 text-sm mb-3 block font-medium">Number of Bags *</label>
                  <div className="grid grid-cols-5 gap-2">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        onClick={() => setFormData(prev => ({ ...prev, numberOfBags: String(n) }))}
                        className={`p-4 rounded-xl border text-center transition-all ${
                          formData.numberOfBags === String(n)
                            ? 'bg-gradient-to-br from-[#8B5CF6]/20 to-[#F4C2C2]/20 border-[#8B5CF6] text-white'
                            : 'bg-white/5 border-white/10 text-white/60 hover:border-white/30'
                        }`}
                        data-testid={`bags-${n}`}
                      >
                        <span className="text-xl font-bold">{n}</span>
                        <span className="text-xs block mt-1 opacity-60">~{n * 10}lbs</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-white/40 text-xs mt-2">Need more? Select 5 and add details in preferences</p>
                </div>

                {/* Return Preference */}
                <div>
                  <label className="text-white/70 text-sm mb-3 block font-medium">Return Preference *</label>
                  <div className="space-y-2">
                    {returnOptions.map(option => (
                      <button
                        key={option.id}
                        onClick={() => setFormData(prev => ({ ...prev, returnPreference: option.id }))}
                        className={`w-full p-4 rounded-xl border text-left transition-all duration-300 flex justify-between items-center ${
                          formData.returnPreference === option.id
                            ? 'bg-gradient-to-br from-[#8B5CF6]/20 to-[#F4C2C2]/20 border-[#8B5CF6]'
                            : 'bg-white/[0.02] border-white/10 hover:border-white/30'
                        }`}
                        data-testid={`return-${option.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {option.badge && (
                            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                              option.badge === 'EXPRESS' ? 'bg-amber-500/20 text-amber-400' : 'bg-[#8B5CF6]/20 text-[#8B5CF6]'
                            }`}>
                              {option.badge}
                            </span>
                          )}
                          <div>
                            <p className={`font-semibold ${formData.returnPreference === option.id ? 'text-white' : 'text-white/80'}`}>
                              {option.label}
                            </p>
                            <p className={`text-xs ${formData.returnPreference === option.id ? 'text-white/60' : 'text-white/40'}`}>
                              {option.description}
                            </p>
                          </div>
                        </div>
                        {option.fee > 0 && (
                          <span className="text-amber-400 font-bold">+${option.fee}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pickup Schedule */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/70 text-sm mb-2 block font-medium">Pickup Date *</label>
                    <Input
                      type="date"
                      name="pickupDate"
                      value={formData.pickupDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl [color-scheme:dark]"
                      data-testid="pickup-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-white/70 text-sm mb-2 block font-medium">Pickup Time *</label>
                    <Input
                      type="time"
                      name="pickupTime"
                      value={formData.pickupTime}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl [color-scheme:dark]"
                      data-testid="pickup-time-input"
                    />
                  </div>
                </div>

                {/* Preferences */}
                <div>
                  <label className="text-white/70 text-sm mb-2 block font-medium">Special Preferences (optional)</label>
                  <textarea
                    name="preferences"
                    value={formData.preferences}
                    onChange={handleChange}
                    placeholder="Fragrance-free detergent, separate colors, etc."
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder:text-white/50 focus:border-[#8B5CF6] focus:outline-none resize-none"
                    data-testid="preferences-input"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Confirm */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8B5CF6]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#8B5CF6]/30">
                    <CheckCircle className="w-6 h-6 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Confirm Your Order</h2>
                    <p className="text-white/50 text-sm">Review your laundry pickup</p>
                  </div>
                </div>
                
                {/* Price Summary Card */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#8B5CF6]/20 via-[#F4C2C2]/10 to-[#8B5CF6]/20 border border-[#8B5CF6]/30 rounded-2xl p-6">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#8B5CF6]/20 rounded-full blur-3xl" />
                  <h3 className="text-lg font-bold text-white mb-4 relative z-10">Price Estimate</h3>
                  <div className="space-y-3 text-white/80 relative z-10">
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-2">
                        <Shirt className="w-4 h-4 text-[#8B5CF6]" />
                        Laundry (~{priceEstimate?.estimated_weight || parseInt(formData.numberOfBags) * 10} lbs)
                      </span>
                      <span className="font-semibold">${priceEstimate?.laundry_cost?.toFixed(2) || '25.00'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#F4C2C2]" />
                        Pickup & Delivery
                      </span>
                      <span className="font-semibold">${priceEstimate?.pickup_delivery_fee?.toFixed(2) || '10.00'}</span>
                    </div>
                    {priceEstimate?.rush_fee > 0 && (
                      <div className="flex justify-between items-center text-amber-400">
                        <span className="flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Same-Day Rush
                        </span>
                        <span className="font-semibold">+${priceEstimate.rush_fee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-white/20 pt-3 mt-3 flex justify-between items-center">
                      <span className="text-xl font-bold text-white">Estimated Total</span>
                      <span className="text-2xl font-bold bg-gradient-to-r from-[#8B5CF6] to-[#F4C2C2] bg-clip-text text-transparent">
                        ${priceEstimate?.total?.toFixed(2) || '35.00'}
                      </span>
                    </div>
                  </div>
                  <p className="text-white/40 text-xs mt-4 relative z-10">
                    * Final price based on actual weight. Receipt provided after processing.
                  </p>
                </div>

                {/* Order Summary */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${selectedService?.color}20` }}>
                      <span className="text-xl">{selectedService?.icon}</span>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Service</p>
                      <p className="text-white font-medium">{selectedService?.label}</p>
                      <p className="text-white/60 text-sm">{formData.numberOfBags} bag(s) • ~{parseInt(formData.numberOfBags) * 10} lbs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#8B5CF6]" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Pickup</p>
                      <p className="text-white font-medium">{formData.pickupAddress}</p>
                      <p className="text-white/60 text-sm">{formData.pickupCity}, NC</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-white/70" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Pickup Time</p>
                      <p className="text-white font-medium">{formData.pickupDate} at {formData.pickupTime}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <Shirt className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Return</p>
                      <p className="text-white font-medium">{returnOptions.find(o => o.id === formData.returnPreference)?.label}</p>
                      <p className="text-white/60 text-sm">{formData.sameAsPickup ? 'Same address as pickup' : `${formData.returnAddress}, ${formData.returnCity}`}</p>
                    </div>
                  </div>
                </div>

                <p className="text-white/40 text-sm text-center">
                  You'll be redirected to secure payment after confirming.
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="border-white/20 text-white hover:bg-white/10 rounded-xl px-6"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  className="bg-gradient-to-r from-[#8B5CF6] to-[#F4C2C2] hover:from-[#7C3AED] hover:to-[#F4C2C2] text-white rounded-xl px-6 shadow-lg shadow-[#8B5CF6]/30"
                  disabled={
                    (step === 1 && (!formData.customerName || !formData.customerPhone || !formData.customerEmail)) ||
                    (step === 2 && (!formData.pickupAddress || !formData.pickupCity))
                  }
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : step === 3 ? (
                <Button
                  onClick={calculateEstimate}
                  className="bg-gradient-to-r from-[#8B5CF6] to-[#F4C2C2] hover:from-[#7C3AED] hover:to-[#F4C2C2] text-white rounded-xl px-6 shadow-lg shadow-[#8B5CF6]/30"
                  disabled={loading || !formData.pickupDate || !formData.pickupTime}
                  data-testid="get-price-btn"
                >
                  {loading ? 'Calculating...' : 'Get Price'}
                  <DollarSign className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] hover:from-[#E6C965] hover:to-[#F4C2C2] text-black font-bold rounded-xl px-8 shadow-lg shadow-[#D4AF37]/30"
                  disabled={loading}
                  data-testid="book-pay-btn"
                >
                  {loading ? 'Processing...' : 'Book & Pay'}
                  <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LaundryBookingPage;
